import { useTranslation } from "react-i18next";
import { formattedDate } from "../../utils";
import { BoxWithDividerLines } from "../BoxWithDividerLines";

import styles from "./BillingPaymentsList.module.scss";
import { ContentWithTitle } from "../ContentWithTitle";
import { formatCurrency } from "../../utils/currency";
import { BillingPaymentPeriod, BillingPaymentsPayment } from "../../types/billing";
import classNames from "classnames";
import { Box } from "../Box";

interface BillingPaymentsListProps {
  payments: BillingPaymentsPayment[];
}

export const BillingPaymentsList = ({ payments }: BillingPaymentsListProps) => {
  const { t } = useTranslation();

  const createControlBoxItem = (title: string, value: string | number, className?: string) => ({
    id: title,
    element: (
      <div className={styles.item}>
        <div className={styles.itemTitle}>{title}</div>
        <div className={classNames(styles.itemValue, className && styles[className])}>{value}</div>
      </div>
    ),
  });

  const createItems = (payment: BillingPaymentsPayment) => {
    const res = [];

    if (payment.period !== BillingPaymentPeriod.Annual) {
      res.push({
        title: "paymentPeriod",
        value: t(`enums.billingPaymentPeriods.${payment.period}`),
      });
    }

    return [
      ...res,
      {
        title: "amount",
        value: formatCurrency(payment?.amount ?? 0),
      },
      {
        title: "state",
        value: t(`enums.billingPaymentStatus.${payment.status}`),
        className: payment.status,
      },
      {
        title: "dueDate",
        value: formattedDate(new Date(payment?.dueDate ?? "")),
      },
    ];
  };

  return (
    <ContentWithTitle title={t("billing.billingPaymentsList.title")}>
      {payments.length === 0 && <Box>{t("billing.billingPaymentsList.noPayments")}</Box>}
      {payments.length > 0 &&
        payments.map((payment, index) => (
          <BoxWithDividerLines
            key={index}
            style={{ padding: "0.8rem 1rem", overflow: "hidden" }}
            items={createItems(payment).map(({ title, value, className }) =>
              createControlBoxItem(t(`billing.billingPaymentsList.${title}`), value, className)
            )}
          />
        ))}
    </ContentWithTitle>
  );
};
